import { IgnoredErrors } from "@/utils/constants/ErrorConstants";
import { jwtDecode } from "jwt-decode";
import { flatten } from "lodash-es";
import { serializeQuery } from "@/utils/UrlUtils";

export const ApiBaseUrl = import.meta.env.VITE_ENDPOINT;
export const ConduitApiBaseUrl = import.meta.env.VITE_CONDUIT_ENDPOINT;

// ─── Helpers ────────────────────────────────────────────────────────────────

const redirectToLogin = () => {
  const location = {
    pathname: window.location.pathname,
    hash: window.location.hash,
    search: window.location.search,
  };
  window.location = `/login?redirect=${encodeURIComponent(JSON.stringify(location))}`;
};

const AUTH_BYPASS_PATHS = ["/invitation", "/password", "/register"];
const isAuthBypassPath = () =>
  AUTH_BYPASS_PATHS.findIndex(p => p === window.location.pathname) >= 0;

function injectAuthHeaders(opts) {
  const idToken = localStorage.getItem("id_token");
  const hasIdToken = !!(idToken && idToken.length);

  if (!opts.headers) opts.headers = {};

  // AWS Marketplace signup — bypass authentication
  if (/aws_marketplace(?!$)/g.test(window.location.pathname)) return null;

  if (!isAuthBypassPath()) {
    if (hasIdToken) {
      try {
        const exp = jwtDecode(idToken).exp;
        if (exp < Date.now() / 1000) {
          if (window.location.pathname !== "/login") {
            localStorage.setItem("id_token", "");
            redirectToLogin();
          }
          return opts;
        }
      } catch (_) {
        if (window.location.pathname !== "/login") {
          localStorage.setItem("id_token", "");
          redirectToLogin();
        }
        return opts;
      }
      opts.headers.Authorization = `Bearer ${idToken}`;
    } else if (window.location.pathname !== "/login") {
      localStorage.setItem("id_token", "");
      redirectToLogin();
    }
  }

  return opts;
}

function handleResponse(res, { isConduit = false } = {}) {
  if (!isConduit) {
    const contextChanged = res.headers.get("X-Company-Context-Changed");
    if (contextChanged) handleAutoContextSwitch(contextChanged);
  }

  if (res.ok) {
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return res.json().then(data => {
        if (data?.success === false) {
          const message = data.error || data.message || IgnoredErrors.REFRESH_REQUIRED.value;
          return Promise.reject(new Error(message));
        }
        return data;
      });
    }
    return res.text().then(text => ({ body: text }));
  }

  if (res.status === 401) {
    if (!isConduit && !isAuthBypassPath() && window.location.pathname !== "/login") {
      localStorage.setItem("id_token", "");
      redirectToLogin();
    } else {
      return Promise.reject(new Error(IgnoredErrors.LOGIN_REQUIRED.value));
    }
  } else if (res.status === 500) {
    return res
      .json()
      .then(data => {
        const pinpointMatch = !isConduit && data.exception?.match(/#<RfAws::PinpointError: (.*)>/);
        return Promise.reject(
          new Error(pinpointMatch ? pinpointMatch[1] : IgnoredErrors.REFRESH_REQUIRED.value),
        );
      })
      .catch(err => {
        if (err instanceof SyntaxError) {
          return Promise.reject(new Error(IgnoredErrors.REFRESH_REQUIRED.value));
        }
        return Promise.reject(err);
      });
  } else if (res.status === 403) {
    return Promise.reject(new Error(IgnoredErrors.ACCESS_DENIED.value));
  } else if (res.status === 404) {
    return Promise.reject(new Error(IgnoredErrors.PAGE_NOT_FOUND.value, { cause: 404 }));
  } else {
    return res.text().then(text => {
      try {
        const error = JSON.parse(text);
        const key = Object.keys(error)[0];
        text = flatten([error[key]]).join(", ");
      } catch (_) {
        return Promise.reject(new Error(IgnoredErrors.REFRESH_REQUIRED.value));
      }
      return Promise.reject(new Error(text));
    });
  }

  return null;
}

function handleAutoContextSwitch(companySlug) {
  Promise.all([import("@/pinia/companiesStore"), import("@/pinia/userStore")])
    .then(([{ useCompaniesStore }, { useUserStore }]) => {
      const companiesStore = useCompaniesStore();
      const userStore = useUserStore();
      let company = companiesStore.accessibleCompanies.find(c => c.slug === companySlug);

      if (!company && companiesStore.accessibleCompanies.length === 0) {
        companiesStore.getAccessibleCompanies(userStore.currUser?.id).then(() => {
          company = companiesStore.accessibleCompanies.find(c => c.slug === companySlug);
          if (company) setCompanyContext(companiesStore, company);
          else console.error(`[Auto-Context] Company ${companySlug} not found`);
        });
      } else if (company) {
        setCompanyContext(companiesStore, company);
      } else {
        console.error(`[Auto-Context] Company ${companySlug} not found`);
      }
    })
    .catch(err => console.error("[Auto-Context] Failed to load stores", err));
}

function setCompanyContext(companiesStore, company) {
  if (companiesStore.viewingCompanySlug === company.slug) return;
  companiesStore.setViewingCompany({
    slug: company.slug,
    name: company.name,
    role: company.acting_role,
    auto: true,
  });
  console.info(`[Auto-Context] Switched to ${company.name} (${company.slug})`);
}

// ─── URL / options builders ──────────────────────────────────────────────────

function appendQuery(url, query) {
  if (!query) return url;
  const qs = serializeQuery(query);
  if (!qs) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${qs}`;
}

const JSON_HEADERS = { "Accept": "application/json", "Content-Type": "application/json" };

function buildFetchOptions(method, body, headers, signal) {
  const opts = {};
  if (method) opts.method = method;
  if (signal) opts.signal = signal;
  if (body !== undefined) {
    opts.headers = { ...JSON_HEADERS, ...headers };
    opts.body = JSON.stringify(body);
  } else if (headers) {
    opts.headers = headers;
  }
  return opts;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Fetch for the main API.
 *
 * @param {string} url
 * @param {object} [options]
 * @param {string}   [options.method]      - HTTP method; defaults to GET
 * @param {AbortSignal} [options.signal]      - Optional AbortSignal to cancel the request
 * @param {*}        [options.body]        - Request payload; auto-serialized to JSON
 * @param {object}   [options.query]       - Query params object appended to URL
 * @param {object}   [options.headers]     - Extra/override headers
 * @param {boolean}  [options.anon=false]  - Skip auth injection; use for public endpoints
 * @param {function} [options.fetchFn]     - Custom fetch implementation (e.g. window.AwsWafIntegration?.fetch)
 * @param {function} [options.onResponse]  - Override the default response handler to access the raw Response object
 */
export function apiFetch(
  url,
  { method, signal, body, query, headers, anon = false, fetchFn, onResponse } = {},
) {
  const opts = buildFetchOptions(method, body, headers, signal);

  if (anon) {
    return (fetchFn ?? fetch)(appendQuery(url, query), opts).then(onResponse ?? handleResponse);
  }

  const viewingCompanySlug = localStorage.getItem("viewing_company_slug");
  if (viewingCompanySlug) {
    opts.headers = { ...opts.headers, "X-Company-Context": viewingCompanySlug };
  }
  const injected = injectAuthHeaders(opts);
  if (injected === null) return null;
  return (fetchFn ?? fetch)(appendQuery(url, query), injected).then(onResponse ?? handleResponse);
}

/**
 * Authenticated fetch for the Conduit API.
 *
 * @param {string} url
 * @param {object} [options]
 * @param {string} [options.method]  - HTTP method; defaults to GET
 * @param {AbortSignal} [options.signal]  - Optional AbortSignal to cancel the request
 * @param {*}      [options.body]    - Request payload; auto-serialized to JSON
 * @param {object} [options.query]   - Query params object appended to URL
 * @param {object} [options.headers] - Extra/override headers
 */
export function conduitApiFetch(
  url,
  { method, signal, body, query, headers, anon = false, onResponse } = {},
) {
  const opts = buildFetchOptions(method, body, headers, signal);
  if (!anon) {
    const idToken = localStorage.getItem("id_token");
    if (idToken?.length) {
      if (!opts.headers) opts.headers = {};
      opts.headers.Authorization = `Bearer ${idToken}`;
    }
  }
  return fetch(appendQuery(url, query), opts).then(
    onResponse ?? (res => handleResponse(res, { isConduit: true })),
  );
}
